// ═══════════════════════════════════════════════════════════════
//   V19.6 — Reconciliation Visibility (admin-only)
// ═══════════════════════════════════════════════════════════════
//
// Firm-wide view of QBO bank/credit-card reconciliation status.
// Two panes:
//   TOP    — header card + "Send digest now" action + thresholds.
//   BOTTOM — table of clients, each row expandable to show per-account
//            last-reconciled date + RAG status.
//
// Data: GET /api/recon/firmwide?deep=true
// Send: POST /api/recon/digest/send

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, RefreshCw, Mail, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle, Clock, Building2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = {
  primary: '#0085CA',
  dark: '#1A1D21',
  muted: '#6B7280',
  card: '#FFFFFF',
  border: '#E5E7EB',
  off: '#F4F6F8',
  red: '#DC2626',
  redSoft: '#FEE2E2',
  yellow: '#D97706',
  yellowSoft: '#FEF3C7',
  green: '#059669',
  greenSoft: '#D1FAE5',
};

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('smj_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function fmtCurrency(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
}

function StatusChip({ status, label }) {
  const map = {
    red: { bg: COLORS.redSoft, fg: COLORS.red },
    yellow: { bg: COLORS.yellowSoft, fg: COLORS.yellow },
    green: { bg: COLORS.greenSoft, fg: COLORS.green },
  };
  const t = map[status] || { bg: '#E5E7EB', fg: '#6B7280' };
  return (
    <span
      data-testid={`status-chip-${status}`}
      style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 999,
        background: t.bg, color: t.fg, fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}
    >
      {label || status}
    </span>
  );
}

function SummaryBadges({ summary }) {
  const items = [
    { key: 'red', label: 'red', count: summary?.red || 0 },
    { key: 'yellow', label: 'yellow', count: summary?.yellow || 0 },
    { key: 'green', label: 'green', count: summary?.green || 0 },
  ].filter(x => x.count > 0);
  if (items.length === 0) {
    return <span style={{ color: COLORS.muted, fontSize: 12 }}>no bank/CC accounts</span>;
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {items.map(i => <StatusChip key={i.key} status={i.key} label={`${i.count} ${i.label}`} />)}
    </div>
  );
}

function ClientRow({ row, expanded, onToggle }) {
  const hasError = !!row.error;
  return (
    <>
      <tr
        data-testid={`recon-row-${row.client_id}`}
        onClick={onToggle}
        style={{ cursor: 'pointer', background: expanded ? COLORS.off : 'transparent' }}
      >
        <td style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, width: 32 }}>
          {expanded
            ? <ChevronDown size={14} color={COLORS.muted} />
            : <ChevronRight size={14} color={COLORS.muted} />}
        </td>
        <td style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontWeight: 600, color: COLORS.dark, fontSize: 13 }}>
            {row.company || '(unnamed)'}
          </div>
          <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 2 }}>
            {row.partner_name || '(unassigned)'}
          </div>
        </td>
        <td style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
          {hasError
            ? <span style={{ color: COLORS.red, fontSize: 12 }}>
                <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
                {row.error}
              </span>
            : <SummaryBadges summary={row.summary} />}
        </td>
        <td style={{
          padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`,
          textAlign: 'right', color: COLORS.dark, fontVariantNumeric: 'tabular-nums',
        }}>
          {row.oldest_days !== null && row.oldest_days !== undefined ? `${row.oldest_days}d` : '—'}
        </td>
      </tr>
      {expanded && !hasError && (
        <tr>
          <td colSpan={4} style={{ background: COLORS.off, padding: '4px 14px 16px 46px', borderBottom: `1px solid ${COLORS.border}` }}>
            {(row.accounts || []).length === 0 ? (
              <div style={{ color: COLORS.muted, fontSize: 12, padding: '12px 0' }}>
                No bank or credit card accounts in QBO.
              </div>
            ) : (
              <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
                <thead>
                  <tr style={{ color: COLORS.muted, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>Account</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>Type</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500, textAlign: 'right' }}>Balance</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>Last reconciled</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500, textAlign: 'right' }}>Age</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {row.accounts.map(acc => (
                    <tr key={acc.id} data-testid={`recon-account-${row.client_id}-${acc.id}`}>
                      <td style={{ padding: '6px 8px', color: COLORS.dark }}>{acc.name}</td>
                      <td style={{ padding: '6px 8px', color: COLORS.muted }}>{acc.qbo_type}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: COLORS.dark }}>
                        {fmtCurrency(acc.current_balance)}
                      </td>
                      <td style={{ padding: '6px 8px', color: COLORS.dark }}>
                        {acc.last_reconciled_on || <span style={{ color: COLORS.muted }}>unknown</span>}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: COLORS.dark }}>
                        {acc.days_since_reconciled !== null ? `${acc.days_since_reconciled}d` : '—'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <StatusChip status={acc.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(row.accounts || []).some(a => a.signal_quality !== 'high') && !hasError && (
              <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 10, fontStyle: 'italic' }}>
                Note: QBO doesn't expose a native LastReconciled field on most realms.
                Dates above are derived from the most recent reconciled transaction.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ReconciliationPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [deep, setDeep] = useState(true);

  const load = async (deepMode = deep) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/api/recon/firmwide?deep=${deepMode}`, authHeaders());
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reconciliation data');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'admin') return;
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const totals = useMemo(() => {
    let red = 0, yellow = 0, green = 0;
    (data?.clients || []).forEach(c => {
      red += c.summary?.red || 0;
      yellow += c.summary?.yellow || 0;
      green += c.summary?.green || 0;
    });
    return { red, yellow, green };
  }, [data]);

  const sendDigest = async () => {
    if (!window.confirm('Send the weekly reconciliation digest to all admins now?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/recon/digest/send`, {}, authHeaders());
      toast.success(`Digest sent to ${res.data.recipients.length} recipient(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={32} className="animate-spin" color={COLORS.primary} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.off, fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: COLORS.card, borderBottom: `1px solid ${COLORS.border}`, padding: '16px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '6px 12px', border: `1px solid ${COLORS.border}`,
                background: 'transparent', borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, color: COLORS.muted, fontSize: 12,
              }}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div>
              <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: COLORS.dark }}>
                Reconciliation Visibility
              </div>
              <div style={{ color: COLORS.muted, fontSize: 12, marginTop: 2 }}>
                Firm-wide QBO bank &amp; credit card reconciliation status
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              data-testid="refresh-recon"
              onClick={() => load(deep)}
              disabled={busy}
              style={{
                padding: '8px 14px', border: `1px solid ${COLORS.border}`,
                background: COLORS.card, borderRadius: 6, cursor: busy ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, color: COLORS.dark, fontSize: 12,
              }}
            >
              <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              data-testid="send-digest-btn"
              onClick={sendDigest}
              disabled={sending || busy}
              style={{
                padding: '8px 14px', border: 'none',
                background: COLORS.primary, borderRadius: 6, cursor: sending ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, color: 'white', fontSize: 12, fontWeight: 600,
              }}
            >
              <Mail size={14} /> {sending ? 'Sending…' : 'Send digest now'}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 40px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Stat tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          <div
            data-testid="stat-red"
            style={{ background: COLORS.redSoft, borderRadius: 8, padding: 16, textAlign: 'center' }}
          >
            <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.red, fontFamily: 'Manrope, sans-serif' }}>
              {totals.red}
            </div>
            <div style={{ fontSize: 11, color: COLORS.red, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <AlertTriangle size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
              over {data?.thresholds?.red_days || 35} days
            </div>
          </div>
          <div
            data-testid="stat-yellow"
            style={{ background: COLORS.yellowSoft, borderRadius: 8, padding: 16, textAlign: 'center' }}
          >
            <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.yellow, fontFamily: 'Manrope, sans-serif' }}>
              {totals.yellow}
            </div>
            <div style={{ fontSize: 11, color: COLORS.yellow, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <Clock size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
              stale ({data?.thresholds?.yellow_days || 15}d+)
            </div>
          </div>
          <div
            data-testid="stat-green"
            style={{ background: COLORS.greenSoft, borderRadius: 8, padding: 16, textAlign: 'center' }}
          >
            <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.green, fontFamily: 'Manrope, sans-serif' }}>
              {totals.green}
            </div>
            <div style={{ fontSize: 11, color: COLORS.green, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <CheckCircle size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
              current
            </div>
          </div>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: COLORS.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              data-testid="deep-scan-toggle"
              checked={deep}
              onChange={e => { setDeep(e.target.checked); load(e.target.checked); }}
            />
            Deep scan (queries transactions when QBO doesn't expose LastReconciled)
          </label>
          {data?.generated_at && (
            <span style={{ fontSize: 11, color: COLORS.muted, marginLeft: 'auto' }}>
              Updated {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
        </div>

        {/* Table */}
        <div data-testid="recon-table" style={{
          background: COLORS.card, borderRadius: 8, border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
        }}>
          {busy && !data && (
            <div style={{ padding: 40, textAlign: 'center', color: COLORS.muted, fontSize: 13 }}>
              <Loader2 size={20} className="animate-spin" style={{ display: 'inline-block', marginRight: 8, verticalAlign: -4 }} />
              Scanning QuickBooks reconciliations…
            </div>
          )}
          {data && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: COLORS.off, textAlign: 'left' }}>
                  <th style={{ padding: '10px 14px', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, width: 32 }} />
                  <th style={{ padding: '10px 14px', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <Building2 size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                    Client
                  </th>
                  <th style={{ padding: '10px 14px', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Status
                  </th>
                  <th style={{ padding: '10px 14px', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>
                    Oldest
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data.clients || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 40, textAlign: 'center', color: COLORS.muted, fontSize: 13 }}>
                      No QuickBooks-connected clients yet.
                    </td>
                  </tr>
                ) : (
                  data.clients.map(c => (
                    <ClientRow
                      key={c.client_id}
                      row={c}
                      expanded={!!expanded[c.client_id]}
                      onToggle={() => setExpanded(p => ({ ...p, [c.client_id]: !p[c.client_id] }))}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: COLORS.muted, lineHeight: 1.5 }}>
          Thresholds: green ≤ {data?.thresholds?.yellow_days || 15}d · yellow {(data?.thresholds?.yellow_days || 15) + 1}–{data?.thresholds?.red_days || 35}d · red &gt; {data?.thresholds?.red_days || 35}d.
          The weekly Monday email digest is triggered by an external scheduler hitting <code>POST /api/recon/digest/send</code>;
          use <b>Send digest now</b> above to deliver one immediately.
        </div>
      </div>
    </div>
  );
}
